import { type JSX, useMemo } from 'react';
import { FormHelperText, FormLabel, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { Listbox } from '@features/effector-form/components/listbox';
import { withControl } from '@features/effector-form/hoc/withControl';
import { SelectOption } from '../multiple-select/types';
import * as Styled from './styled';
import { SelectControlType } from './types';

export const SelectControl = withControl<SelectControlType>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    options = [],
    placeholder,
    isLoading,
    style = {},
    isError,
    multiple,
    clearable = true,
    ...props
  }) => {
    const id = useMemo(() => {
      if (!multiple) {
        const key = typeof value === 'object' ? value?.id : value;

        return Array.isArray(options) && options.length > 0 && key
          ? options.find((list) => list.id === key)
          : null;
      }

      return Array.isArray(options) && options.length > 0
        ? options.filter(({ id }) => value.includes(id))
        : [];
    }, [options, value]);

    return (
      <Styled.CustomFormControl
        required={!readOnly && required}
        disabled={disabled}
        style={style}
      >
        {label && <FormLabel error={isError}>{label}</FormLabel>}
        <Autocomplete
          {...props}
          value={id}
          options={!options ? [] : options}
          onChange={(_event: any, newValue: any) => {
            if (!multiple) {
              setValue(newValue?.id || null);
              return;
            }

            setValue((newValue as SelectOption[]).map(({ id }) => id));
          }}
          disableClearable={!clearable}
          multiple={multiple}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              placeholder={placeholder}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {isLoading && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Styled.CustomFormControl>
    );
  }
);
